import React, { useState } from 'react';
import { Card, Table, Form, Select, Button, Space, Badge, Typography, Popconfirm, message } from 'antd';
import { useTableRequest } from '@/hooks';
import {
  getCustomerDeviceList,
  unbindCustomerDevice,
  CustomerDeviceQueryResponse,
} from '@/api/customer';
import { getCustomerUsers } from '@/api/workorder';
import { getDevice } from '@/api';
import { useRequest } from 'ahooks';
import dayjs from 'dayjs';
import BindModal from './BindModal';

const { Text } = Typography;

const statusMap: Record<number, { color: string; label: string }> = {
  1: { color: 'green', label: '绑定中' },
  0: { color: 'gray', label: '已解绑' },
};

const CustomerDevicePage: React.FC = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [bindOpen, setBindOpen] = useState(false);

  const { data: deviceData } = useRequest(getDevice, { defaultParams: [{ isAll: true }] });
  const { data: customerData, refresh: refreshCustomers } = useRequest(getCustomerUsers, { manual: true });

  const { loading, dataSource, pagination, form, reload, search, reset } = useTableRequest<CustomerDeviceQueryResponse>({
    onRequest: (params) => {
      return getCustomerDeviceList(params);
    },
  });

  const handleUnbind = async (record: CustomerDeviceQueryResponse) => {
    const resp = await unbindCustomerDevice({
      customerId: record.customerId,
      deviceId: record.deviceId,
    });
    if (resp.success) {
      messageApi.success('解绑成功');
      reload();
    } else {
      messageApi.error(resp.errorMsg || '解绑失败');
    }
  };

  return (
    <>
      {contextHolder}
      <Form layout="inline" className="ml20 mt20" form={form}>
        <Form.Item key="customerId" name="customerId">
          <Select
            placeholder="选择客户"
            style={{ width: 160 }}
            allowClear
            showSearch
            optionFilterProp="label"
            options={(customerData?.result || []).map((c: any) => ({
              value: c.id,
              label: c.nickName || c.username,
            }))}
            onFocus={() => refreshCustomers()}
          />
        </Form.Item>
        <Form.Item key="deviceId" name="deviceId">
          <Select
            options={deviceData?.result?.list || []}
            allowClear
            showSearch
            optionFilterProp="name"
            placeholder="选择设备"
            style={{ width: 160 }}
            fieldNames={{ label: 'name', value: 'id' }}
          />
        </Form.Item>
        <Form.Item>
          <Button type="primary" onClick={search}>查询</Button>
        </Form.Item>
        <Form.Item>
          <Button type="default" onClick={reset}>重置</Button>
        </Form.Item>
        <Form.Item>
          <Button type="primary" onClick={() => setBindOpen(true)}>绑定设备</Button>
        </Form.Item>
      </Form>

      <Card className="m20" size="small">
        <Table
          loading={loading}
          dataSource={dataSource}
          pagination={pagination}
          rowKey="id"
          size="small"
          columns={[
            {
              title: '客户ID',
              key: 'customerId',
              dataIndex: 'customerId',
              width: 200,
              ellipsis: true,
            },
            {
              title: '设备名称',
              key: 'deviceName',
              dataIndex: 'deviceName',
              width: 150,
              render: (value) => value || '-',
            },
            {
              title: '设备ID',
              key: 'deviceId',
              dataIndex: 'deviceId',
              width: 200,
              ellipsis: true,
            },
            {
              title: '绑定时间',
              key: 'bindTime',
              dataIndex: 'bindTime',
              width: 160,
              render: (value) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '-'),
            },
            {
              title: '状态',
              key: 'status',
              dataIndex: 'status',
              width: 80,
              render: (value) => {
                const s = statusMap[value];
                return s ? (
                  <>
                    <Badge color={s.color} />
                    <Text className="ml5">{s.label}</Text>
                  </>
                ) : '-';
              },
            },
            {
              title: '操作',
              key: 'action',
              width: 80,
              render: (_, record) =>
                record.status === 1 ? (
                  <Popconfirm
                    title="确定解绑此设备？"
                    okText="确定"
                    cancelText="取消"
                    onConfirm={() => handleUnbind(record)}
                  >
                    <Typography.Link type="danger">解绑</Typography.Link>
                  </Popconfirm>
                ) : null,
            },
          ]}
        />
      </Card>

      <BindModal
        open={bindOpen}
        type="customer"
        onClose={() => setBindOpen(false)}
        onSuccess={() => { reload(); setBindOpen(false); }}
      />
    </>
  );
};

export default CustomerDevicePage;
